
import React, { useRef, useState, useEffect } from 'react';
import axios from 'axios';

import {
    BsPlayFill as PlayArrow, BsPause as Pause, BsVolumeUp as VolumeUp, BsVolumeOff as VolumeOff,
    BsFullscreen as Fullscreen,
    BsDownload,
    BsCloudDownloadFill,
    BsVolumeOffFill,
    BsVolumeUpFill,
    BsSpeedometer,
    BsX,
    BsCalendar
} from "react-icons/bs";

import {
    Box, Slider, IconButton, Stack, Select, MenuItem, Typography
} from '@mui/material';


const VideoPlayer2 = ({ src, selectedCamera, date, onAction = () => { }, handleVideoEnd = () => { } }) => {
    const videoRef = useRef(null);
    const [playing, setPlaying] = useState(false);
    const [volume, setVolume] = useState(1);
    const [muted, setMuted] = useState(false);
    const [progress, setProgress] = useState(0);
    const [playbackRate, setPlaybackRate] = useState(1);
    const [m3u8Url, setM3u8Url] = useState();
    const [loading, setLoading] = useState(false);
    const [day, setDay] = useState(1);

    useEffect(() => {
        if (!selectedCamera?.id || !date) return;
        
        setLoading(true);
        setM3u8Url(null);

        // Create m3u8 based on date selection
        const fetchHls = async () => {
            try {
                const res = await axios.get(`video/?camera=${selectedCamera?.id}&date=${date}&days=${day}&version=3`);
                const responseData = res?.data?.data || res?.data;
                if (responseData?.url) {
                    setM3u8Url(responseData.url);
                } else if (typeof responseData === 'string') {
                    setM3u8Url(responseData);
                }
            } catch (err) {
                console.error("Failed to create HLS m3u8:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchHls();

        // Cleanup: delete videos on unmount or date/day change
        return () => {
            axios.delete(`video/?camera=${selectedCamera?.id}&date=${date}&days=${day}`).catch(() => {});
        };
    }, [selectedCamera?.id, date, day]);

    useEffect(() => {
        let hlsInstance;
        const video = videoRef.current;
        if (!video || !m3u8Url) return;

        const loadVideo = async () => {
            if (video.canPlayType('application/vnd.apple.mpegurl')) {
                // Native HLS (Safari / iOS)
                video.src = m3u8Url;
                video.addEventListener('loadedmetadata', () => {
                    video.play().then(() => setPlaying(true)).catch(() => {});
                });
            } else {
                // Use Hls.js for Chrome / Firefox / Edge
                try {
                    let Hls = (await import('hls.js')).default;
                    if (Hls.isSupported()) {
                        hlsInstance = new Hls();
                        hlsInstance.loadSource(m3u8Url);
                        hlsInstance.attachMedia(video);
                        hlsInstance.on(Hls.Events.MANIFEST_PARSED, () => {
                            video.play().then(() => setPlaying(true)).catch(() => {});
                        });
                        hlsInstance.on(Hls.Events.ERROR, (event, data) => {
                            if (data.fatal) {
                                switch (data.type) {
                                    case Hls.ErrorTypes.NETWORK_ERROR:
                                        hlsInstance.startLoad();
                                        break;
                                    case Hls.ErrorTypes.MEDIA_ERROR:
                                        hlsInstance.recoverMediaError();
                                        break;
                                    default:
                                        hlsInstance.destroy();
                                        break;
                                }
                            }
                        });
                    }
                } catch (e) {
                    console.error("Hls.js load failed:", e);
                }
            }
        };

        loadVideo();

        const handleTimeUpdate = () => {
            if (video.duration) {
                setProgress((video.currentTime / video.duration) * 100 || 0);
            }
        };
        video.addEventListener('timeupdate', handleTimeUpdate);

        return () => {
            video.removeEventListener('timeupdate', handleTimeUpdate);
            if (hlsInstance) {
                hlsInstance.destroy();
            }
        };
    }, [m3u8Url]);

    const togglePlay = () => {
        const video = videoRef.current;
        if (!video) return;
        if (video.paused) {
            video.play();
            setPlaying(true);
        } else {
            video.pause();
            setPlaying(false);
        }
    };

    const handleVolumeChange = (_, value) => {
        const vol = Array.isArray(value) ? value[0] : value;
        if (videoRef.current) {
            videoRef.current.volume = vol;
        }
        setVolume(vol);
        setMuted(vol === 0);
    };

    const toggleMute = () => {
        if (videoRef.current) {
            videoRef.current.muted = !muted;
        }
        setMuted(!muted);
    };

    const handleSeek = (_, value) => {
        const video = videoRef.current;
        if (!video || !video.duration) return;
        const newTime = (Array.isArray(value) ? value[0] : value) * video.duration / 100;
        video.currentTime = newTime;
        setProgress((newTime / video.duration) * 100);
    };

    const handleSpeedChange = (event) => {
        const rate = parseFloat(event.target.value);
        if (videoRef.current) {
            videoRef.current.playbackRate = rate;
        }
        setPlaybackRate(rate);
    };

    const handleDownload = () => { }

    const handleClose = () => {
        // Delete videos when close that page
        axios.delete(`video/?camera=${selectedCamera?.id}&date=${date}&days=${day}`).catch(() => {});
        onAction("close");
    };

    return (<>
        <Box sx={{ width: '100%', mx: 'auto', position: 'relative' }}>
            <Box sx={{
                mx: 'auto', left: 0, pt: 10,
                position: 'absolute', zIndex: 999, px: 4, width: 1,
            }}>
                <Box display={'flex'} alignItems={'start'} gap={1}>
                    {[undefined, 7, 30].map((d) => <Box className="box active"
                        key={d || 1}
                        onClick={() => { setDay((d || 1)); }}
                        sx={{
                            fontSize: 12, minWidth: 110, textWrap: 'nowrap',
                            background: '#d7d7d7f0', px: 2, py: 1, borderRadius: 2,
                            cursor: 'pointer'
                        }}><BsCalendar /> Last {d || 1} Day</Box>)}
                    <Box width={1} />
                    <Box>
                        <Box className="box hover" sx={{ fontSize: 12, background: '#000000e0', px: 2, py: 1, borderRadius: 2 }}>
                            <IconButton sx={{ background: 'black', color: '#ffd80c', p: 0 }} onClick={handleClose}>
                                <BsX fontSize={28} />
                            </IconButton>
                        </Box>
                    </Box>
                </Box>

            </Box>
            <Box sx={{
                py: 0, width: '45%', mx: 'auto', left: 0, right: 0,
                background: '#00000073', position: 'absolute', zIndex: 999,
                borderRadius: '0px 0px 10px 10px', p: 1
            }}>
                <Box sx={{ px: 1, width: 1, pt: 2 }}>
                    <Slider
                        sx={{ color: 'white', width: 1, py: 0, }}
                        value={progress}
                        onChange={handleSeek}
                    />
                    <Stack direction="row" sx={{ width: 1, py: 0 }} spacing={1} alignItems="center" >
                        <IconButton onClick={togglePlay} sx={{ background: 'black', color: '#ffd80c', p: 0.5, }}>
                            {playing ? <Pause fontSize={20} /> : <PlayArrow sx={{ ml: 0.4, my: 0.2 }} fontSize={20} />}
                        </IconButton>
                        <Box sx={{ width: 1 }}></Box>

                        <IconButton sx={{ background: 'black', color: '#ffd80c', p: 0.6 }} onClick={toggleMute}>
                            {muted || volume === 0 ? <BsVolumeOffFill fontSize={18} /> : <BsVolumeUpFill fontSize={18} />}
                        </IconButton>

                        <Slider
                            value={volume}
                            min={0}
                            max={1}
                            step={0.01}
                            sx={{ color: 'white', width: 100 }}
                            onChange={handleVolumeChange}
                        />
                        <Select
                            renderValue={() => <IconButton sx={{ background: 'black', color: '#ffd80c', p: 1 }} >
                                <BsSpeedometer fontSize={14} />
                            </IconButton>}
                            value={playbackRate}
                            onChange={handleSpeedChange}
                            size="small"
                            variant="standard"
                            sx={{
                                minWidth: 80, color: 'white', p: 0, fontSize: 13, '& .MuiSelect-select': {
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: 0,
                                },
                            }}
                        >
                            {[0.5, 1, 1.5, 2].map((rate) => (
                                <MenuItem key={rate} value={rate}>
                                    {rate}x
                                </MenuItem>
                            ))}
                        </Select>
                        <IconButton sx={{ background: 'black', color: '#ffd80c', p: 1 }} onClick={() => handleDownload()}>
                            <BsCloudDownloadFill fontSize={13} />
                        </IconButton>
                        <IconButton sx={{ background: 'black', color: '#ffd80c', p: 1 }} onClick={() => videoRef.current && videoRef.current.requestFullscreen()}>
                            <Fullscreen fontSize={13} />
                        </IconButton>

                    </Stack>
                </Box>
            </Box>
            {loading && (
                <Box sx={{ width: '100%', height: 450, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#000000b0', borderRadius: 2 }}>
                    <Typography color="#ffd80c" sx={{ fontSize: 16, fontWeight: 'bold' }}>Creating HLS (m3u8) video stream...</Typography>
                </Box>
            )}
            <video
                ref={videoRef}
                width="100%"
                style={{ borderRadius: 2, display: loading ? 'none' : 'block' }}
                onEnded={() => { handleVideoEnd(videoRef) }}
                playsInline
            />
        </Box>
    </>
    );
};

export default VideoPlayer2;
